// memberState mirrors the apps' shared MemberState (spec 010 FR-009):
// what a member's row, pin, and panel say. testdata/member-state.json holds
// the cases both pass. Its words come from i18n/common, which the apps share
// (spec 013 FR-010).
import type { MemberLocation } from '../api/types';
import { integer, monthDay, t, time, weekday } from '../i18n/index.ts';

export type StateKind = 'PAUSED' | 'NOT_SHARED' | 'MANUAL' | 'PROBLEM' | 'MOVING' | 'AT_PLACE' | 'NEAR' | 'LOCATED';

export interface StateNote {
  text: string;
  severe: boolean;
}

export interface MemberState {
  kind: StateKind;
  headline: string;
  // detail always carries the location's age (constitution III).
  detail: string;
  icon: string;
  // speed is set only while the member moves and their location is current.
  speed: string | null;
  current: boolean;
  lowBattery: boolean;
  notes: StateNote[];
}

// MOVING_MPS is the speed from which a member counts as moving.
export const MOVING_MPS = 1;
// LOW_BATTERY is the level at or under which an uncharged phone is low.
export const LOW_BATTERY = 20;
// STALE_MS is how long a phone may go unheard before its member is out of
// date, the server's StaleAfter. An open map gets no message when time
// passes, so it works this out itself (002 FR-010).
export const STALE_MS = 75 * 60_000;

const movingActivities = new Set(['driving', 'walking', 'running', 'cycling']);

// wakeCause words why a phone could not be woken.
export function wakeCause(code: string): string {
  return t('wakeCause', { code });
}

// unreachable says the member's phone could not be woken: at once when the
// server knows why, or after a minute with no answer.
export function unreachable(m: MemberLocation, now: number): string {
  if (!m.wake) return '';
  if (m.wake.error) return t('unreachableBecause', { member: m.display_name, cause: wakeCause(m.wake.error) });
  if (now - new Date(m.wake.asked_at).getTime() >= 60_000) return t('unreachableNoAnswer', { member: m.display_name });
  return '';
}

// memberState describes a member at now (ms) in a time zone, with speeds in
// miles per hour when miles is set.
export function memberState(m: MemberLocation, now: number, tz: string, miles: boolean): MemberState {
  const fix = m.location;
  const ago = fix ? age(fix.recorded_at, now) : null;
  const quiet = m.last_seen_at != null && now - new Date(m.last_seen_at).getTime() >= STALE_MS;
  const status = m.status === 'current' && quiet ? 'stale' : m.status;
  const notes: StateNote[] = [];
  const cannot = unreachable(m, now);
  if (cannot) notes.push({ text: cannot, severe: true });
  if (!m.crash_detection && status !== 'paused' && status !== 'not_shared') notes.push({ text: t('noteCrashOff'), severe: false });
  // A group of the viewer's sees every fix (spec 001 FR-060).
  if (m.tracking_mode === 'detailed' && status !== 'paused' && status !== 'not_shared') notes.push({ text: t('noteDetailed'), severe: false });
  const lowBattery = fix?.battery_pct != null && fix.battery_pct <= LOW_BATTERY && fix.charging !== true;
  const current = status === 'current';
  const state = (kind: StateKind, headline: string, detail: string, icon: string, speed: string | null = null): MemberState => ({
    kind, headline, detail, icon, speed, current, lowBattery, notes,
  });
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  // A member whose groups with the viewer all have location off is not a
  // problem to fix (spec 001 FR-047).
  if (status === 'not_shared') {
    return state('NOT_SHARED', t('stateNotShared'), t('stateNotSharedDetail'), 'hand');
  }
  if (status === 'paused') {
    const until = m.paused_until ? t('stateUntil', { when: sinceWords(m.paused_until, now, tz) }) : t('stateUntilResume');
    return state('PAUSED', t('statePaused'), until, 'pause');
  }
  if (m.tracking_mode === 'manual') {
    return state('MANUAL', t('stateManual'), ago ? t('stateLastShared', { age: ago }) : t('stateNothingShared'), 'hand');
  }
  if (!fix || status === 'unknown') {
    return state('PROBLEM', t('stateNoLocation'), m.reason ? cap(m.reason) : t('stateNothingReported'), 'question');
  }
  if (status === 'limited') {
    return state('PROBLEM', t('stateLimited'), [m.reason ? cap(m.reason) : null, ago].filter(Boolean).join(' · '), 'exclamation');
  }
  if (status === 'stale') {
    // Why, when the server knows it (spec 010).
    if (m.reason && m.status === 'stale') notes.unshift({ text: cap(m.reason), severe: true });
    return state('PROBLEM', t('stateNotUpdating'), t('stateLastUpdated', { age: ago ?? '' }), 'exclamation');
  }
  const mps = fix.speed_mps ?? 0;
  const moving = !m.since && (mps >= MOVING_MPS || movingActivities.has(fix.activity ?? ''));
  if (moving) {
    const headline = m.near ? t('stateMovingNear', { activity: activityKey(fix.activity), near: m.near }) : doing(fix.activity);
    const sp = mps >= MOVING_MPS ? speed(mps, miles) : null;
    return state('MOVING', headline, [sp, ago].filter(Boolean).join(' · '), activityIcon(fix.activity), sp);
  }
  const detail = [m.since ? t('stateSince', { when: sinceWords(m.since, now, tz) }) : null, ago].filter(Boolean).join(' · ');
  if (m.place) return state('AT_PLACE', t('stateAt', { place: m.place }), detail, m.place_icon ?? 'pin');
  if (m.near) return state('NEAR', m.near, detail, 'pin');
  return state('LOCATED', t('stateLocated'), detail, 'pin');
}

// doing names an activity; a phone that could not tell says Moving.
export function doing(activity?: string): string {
  return t('activityDoing', { activity: activityKey(activity) });
}

// activityKey is the activity as the catalog's select keys name it.
function activityKey(activity?: string): string {
  return activity && movingActivities.has(activity) ? activity : 'other';
}

// activityIcon names the icon for an activity.
export function activityIcon(activity?: string): string {
  switch (activity) {
    case 'driving': return 'car';
    case 'walking': return 'walking';
    case 'running': return 'running';
    case 'cycling': return 'bicycle';
    default: return 'location-arrow';
  }
}

// speed is a speed in the viewer's units, rounded to a whole number.
export function speed(mps: number, miles: boolean): string {
  return miles ? t('speedMph', { speed: integer(Math.round(mps * 2.23694)) }) : t('speedKmh', { speed: integer(Math.round(mps * 3.6)) });
}

// age says how long ago a time was, as every location shows it.
export function age(iso: string, now: number): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return t('ageUnknown');
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 60) return t('ageJustNow');
  if (secs < 3600) return t('ageMinutes', { n: Math.floor(secs / 60) });
  if (secs < 48 * 3600) return t('ageHours', { n: Math.floor(secs / 3600) });
  return t('ageDays', { n: Math.floor(secs / 86400) });
}

// sinceWords names a moment the way rows do: a time today, a time yesterday
// or tomorrow, a weekday within a week either way, or a date, in the
// reader's formats (spec 013 FR-012). Callers put it in a whole message.
export function sinceWords(iso: string, now: number, tz: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const day = (ms: number) => {
    const f = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric' });
    const got: Record<string, string> = {};
    for (const p of f.formatToParts(new Date(ms))) got[p.type] = p.value;
    return Date.UTC(Number(got.year), Number(got.month) - 1, Number(got.day));
  };
  const days = Math.round((day(now) - day(then)) / 86_400_000);
  const at = new Date(then);
  const clock = time(at, tz);
  if (days === 0) return clock;
  if (days === 1) return t('momentYesterday', { time: clock });
  if (days === -1) return t('momentTomorrow', { time: clock });
  if (Math.abs(days) < 7) return weekday(at, false, tz);
  return monthDay(at, tz);
}
